import { useLocation } from "wouter";
import { Link } from "wouter";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import { useState, useEffect } from "react";
import { loadAllArtworks, searchArtworks, getRandomArtwork, StaticArtwork } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";

const THEMES = [
  { slug: "eerie",     label: "Creepy",      description: "Eerie and haunting art for Halloween",    gradient: "from-gray-800 to-purple-900",     emoji: "🕯️" },
  { slug: "wonder",    label: "Wonder",     description: "Art that leaves you speechless",          gradient: "from-indigo-500 to-purple-600",   emoji: "✨" },
  { slug: "joy",       label: "Joy",         description: "The most uplifting paintings in history", gradient: "from-yellow-400 to-orange-500",    emoji: "☀️" },
  { slug: "sadness",   label: "Melancholy",  description: "Art of grief, loss, and longing",         gradient: "from-blue-500 to-slate-600",      emoji: "🌧️" },
  { slug: "nostalgia", label: "Nostalgia",   description: "Art aching with the passage of time",     gradient: "from-amber-500 to-orange-600",    emoji: "🕰️" },
  { slug: "tension",   label: "Tension",     description: "Art that keeps you on edge",              gradient: "from-red-500 to-rose-700",        emoji: "⚡" },
  { slug: "arousal",   label: "Energy",      description: "The most intense and electric artworks",  gradient: "from-orange-500 to-red-600",      emoji: "🔥" },
  { slug: "valence",   label: "Beauty",      description: "The most uplifting and serene art",       gradient: "from-emerald-400 to-teal-600",    emoji: "🌿" },
  { slug: "anger",     label: "Defiance",    description: "Art of rebellion and confrontation",      gradient: "from-red-700 to-red-900",         emoji: "⚔️" },
  { slug: "dominance", label: "Dominance",   description: "Art that overwhelms and commands",        gradient: "from-slate-600 to-gray-900",      emoji: "🏛️" },
  { slug: "interest",  label: "Curiosity",   description: "The most intellectually rich artworks",   gradient: "from-cyan-500 to-blue-600",       emoji: "🔍" },
];

export default function EmotionIndex() {
  const [, setLocation] = useLocation();
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<StaticArtwork[]>([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);

  useEffect(() => {
    loadAllArtworks().then(setComprehensiveArtworks).catch(console.error);
  }, []);

  const handleArtworkSelect = (artworkId: number) => {
    setLocation(`/artwork/${artworkId}/`);
    trackEvent("view_artwork", "navigation", "emotion_index", artworkId);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) { setSearchResults([]); return; }
    setSearchResults(searchArtworks(comprehensiveArtworks, query, 20));
  };

  const handleRandomArtwork = () => {
    const art = getRandomArtwork(comprehensiveArtworks);
    if (art?.id) setLocation(`/artwork/${art.id}/`);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Emotion Collections — Ariadne's Gallery"
        description="Browse 116,000 artworks sorted by emotional impact. Find the most joyful, melancholic, tense, or wondrous paintings in art history."
        url="https://ariadnesgallery.com/emotion/"
        type="website"
      />

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onRandomArtwork={handleRandomArtwork}
      />

      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold mb-3">Emotion Collections</h1>
          <p className="text-muted-foreground mb-10 text-lg max-w-2xl">
            Every artwork in our collection has been scored across 10 emotional dimensions.
            Browse the top 50 artworks for each emotion, or{" "}
            <Link href="/emotion-search" className="text-indigo-600 hover:underline">
              search by your own emotional profile
            </Link>.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {THEMES.map((theme) => (
              <Link
                key={theme.slug}
                href={`/emotion/${theme.slug}`}
                className="group block rounded-xl overflow-hidden border hover:shadow-md transition-shadow"
              >
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5 flex items-center gap-4`}>
                  <span className="text-3xl">{theme.emoji}</span>
                  <div>
                    <div className="text-white font-bold text-xl">{theme.label}</div>
                    <div className="text-white/75 text-sm">{theme.description}</div>
                  </div>
                  <div className="ml-auto text-white/60 group-hover:text-white transition-colors text-lg">
                    →
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 p-6 bg-gray-50 rounded-xl border">
            <h2 className="font-semibold mb-2">Find art by your own emotional profile</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Use the emotion search tool to dial in exactly the mix of emotions you're looking for.
            </p>
            <Link
              href="/emotion-search"
              className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              Open Emotion Search
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
